import { orgCalendarDate, orgDayStartToUtc } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';

/** A calendar month, "YYYY-MM". */
export type ReviewMonth = string;

const MONTH = /^(\d{4})-(\d{2})$/;

/** The month `offset` months after `month` ("2026-11" + 2 = "2027-01"). */
export const addMonths = (month: ReviewMonth, offset: number): ReviewMonth => {
  const [, year, mon] = MONTH.exec(month) ?? [];
  const index = Number(year) * 12 + Number(mon) - 1 + offset;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
};

/** The organization's current month (its own calendar, not the browser's). */
export const currentOrgMonth = (
  now: Date = new Date(),
  timeZone: string = getOrganizationTimeZone()
): ReviewMonth => orgCalendarDate(now.toISOString(), timeZone).slice(0, 7);

/** The prototype's month strip: `before` months, the given month, then `after` months, oldest first. */
export const monthsAround = (month: ReviewMonth, before = 6, after = 6): ReviewMonth[] =>
  Array.from({ length: before + after + 1 }, (_, index) => addMonths(month, index - before));

/**
 * The reviews created in `month`: `CreatedDate` is the moment a review was set up (a UTC instant), so the
 * month runs from the first second of its 1st to the first second of the next month's 1st, both in the
 * organization's zone (hrm-timezone-standard, kind C).
 */
export const createdInMonthClause = (
  month: ReviewMonth,
  timeZone: string = getOrganizationTimeZone()
): Record<string, unknown> => ({
  CreatedDate: {
    $gte: orgDayStartToUtc(`${month}-01`, timeZone),
    $lt: orgDayStartToUtc(`${addMonths(month, 1)}-01`, timeZone),
  },
});

/** "Nov 2026" in the given language. */
export const formatReviewMonth = (month: ReviewMonth, locale?: string): string => {
  const [, year, mon] = MONTH.exec(month) ?? [];
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(year), Number(mon) - 1, 1))
  );
};
